<div class="entity-compliance-answers-container">
  <!-- Loading State -->
  <div v-if="is_loading_overview" class="text-center py-5">
    <div class="spinner-border text-primary-dark spinner-border-sm" role="status"></div>
    <p class="text-xxs mt-2 text-muted">Loading compliance data...</p>
  </div>

  <!-- No Data State -->
  <div v-else-if="!has_data" class="text-center py-5 glass-card rounded-lg bg-white shadow-sm border">
    <p class="text-xs text-muted">No compliance rules assigned to this entity.</p>
  </div>

  <!-- Main Content -->
  <div v-else>
    <!-- Header Summary Card -->
    <div class="glass-card border-0 rounded-lg mb-4 p-4 shadow-sm bg-white">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 class="text-sm fw-bold mb-1">Compliance Overview</h5>
          <p class="text-xxs text-muted mb-0">{{ entity_name }} - {{ entity_type_name }}</p>
        </div>
        <div class="d-flex align-items-center gap-2">
          <div class="badge-group me-2">
            <span class="status-badge text-xxs manually-verified px-2 py-1 rounded-small shadow-sm">
              <i class="bi bi-shield-check me-1"></i> {{ total_rules }} Rules
            </span>
          </div>
          <button
            class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow-sm px-3"
            @click="sync_answers"
            :disabled="is_syncing"
          >
            <span v-if="is_syncing" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-arrow-repeat me-1"></i>
            Sync Answers
          </button>
        </div>
      </div>

      <!-- Progress Section -->
      <div class="progress-section mb-2">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <div class="d-flex gap-4">
            <div class="stat-item">
              <span class="text-xxs text-muted d-block">Answered</span>
              <span class="text-xs fw-bold text-success">{{ total_answered }}</span>
            </div>
            <div class="stat-item">
              <span class="text-xxs text-muted d-block">Pending</span>
              <span class="text-xs fw-bold text-orange">{{ total_unanswered }}</span>
            </div>
          </div>
          <div class="text-end">
             <span class="text-xxs text-muted d-block">Completion</span>
             <span class="text-xs fw-bold text-dark">{{ completion_percentage }}%</span>
          </div>
        </div>
        <div class="custom-progress shadow-sm rounded-pill overflow-hidden bg-light" style="height: 8px;">
          <div
            class="custom-progress-bar rounded-pill"
            :style="{ width: completion_percentage + '%', backgroundColor: 'var(--primary-dark)', height: '100%', transition: 'width 0.5s ease' }"
          ></div>
        </div>
      </div>
    </div>

    <!-- Rules Accordion -->
    <div class="rules-container">
      <div v-for="rule_item in filtered_rules" :key="rule_item.id" class="rule-card glass-card border-0 rounded-lg mb-3 shadow-sm bg-white overflow-hidden">
        <!-- Rule Header -->
        <div
          class="rule-header p-3 clickable d-flex justify-content-between align-items-center"
          @click="toggle_rule_expand(rule_item.id)"
          :class="{ 'border-bottom': is_rule_expanded(rule_item.id) }"
          style="cursor: pointer;"
        >
          <div class="d-flex align-items-center gap-3">
            <div class="rule-status-icon">
              <i v-if="rule_item.answered_count === rule_item.total_questions" class="bi bi-check-circle-fill text-success fs-5"></i>
              <i v-else class="bi bi-exclamation-circle-fill text-orange fs-5"></i>
            </div>
            <div class="rule-info">
              <h6 class="text-xs fw-bold mb-0 text-capitalize">{{ rule_item.name }}</h6>
              <p class="text-xs text-muted mb-0" v-if="rule_item.description">{{ truncate_text(rule_item.description, 80) }}</p>
            </div>
          </div>

          <div class="d-flex align-items-center gap-4">
            <!-- Schedule Summary -->
            <div class="schedule-stats d-flex gap-3" v-if="rule_evaluation_dates[rule_item.id]">
              <div class="stat text-end">
                <span class="text-xs text-muted d-block">Last Eval</span>
                <span class="text-xs fw-bold text-dark">{{ rule_evaluation_dates[rule_item.id].last_evaluation ? format_date(rule_evaluation_dates[rule_item.id].last_evaluation) : 'N/A' }}</span>
              </div>
              <div class="stat text-end">
                <span class="text-xs text-muted d-block">Next Due</span>
                <span class="text-xs fw-bold" :class="get_next_evaluation_class(rule_evaluation_dates[rule_item.id].next_evaluation)">
                  {{ rule_evaluation_dates[rule_item.id].next_evaluation ? format_date(rule_evaluation_dates[rule_item.id].next_evaluation) : 'N/A' }}
                </span>
              </div>
            </div>

            <div class="count-badge">
              <span class="status-badge text-xxs py-1 shadow-sm px-2 rounded-small" :class="rule_item.answered_count === rule_item.total_questions ? 'manually-verified' : 'pending'">
                {{ rule_item.answered_count }}/{{ rule_item.total_questions }}
              </span>
            </div>
            <i class="bi text-muted" :class="is_rule_expanded(rule_item.id) ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          </div>
        </div>

        <!-- Rule Body -->
        <div v-if="is_rule_expanded(rule_item.id)" class="rule-body p-0">
          <div v-if="is_rule_loading(rule_item.id)" class="text-center py-4">
            <div class="spinner-border spinner-border-sm text-primary-dark" role="status"></div>
          </div>

          <div v-else-if="get_rule_details(rule_item.id)" class="questions-list">
            <div v-for="sub_section in get_all_sub_sections_for_rule(rule_item.id)" :key="sub_section.section_id">
              <!-- Sub-section Header -->
              <div class="sub-section-title px-3 py-2 bg-light border-bottom d-flex justify-content-between align-items-center">
                <span class="text-x fw-bold text-muted text-uppercase tracking-wider">{{ sub_section.section_name }}</span>
                <span class="text-xs text-muted">{{ sub_section.questions.length }} Items</span>
              </div>

              <!-- Questions -->
              <div class="question-items">
                <div v-for="(question, q_idx) in sub_section.questions" :key="question.section_question_id" class="question-row p-3 border-bottom">
                  <div class="row align-items-start g-3">
                    <div class="col-md-7">
                      <div class="d-flex gap-2 mb-2">
                        <span class="text-xxs fw-bold text-muted">{{ q_idx + 1 }}.</span>
                        <p class="text-xs fw-bold mb-0 text-dark">{{ question.question_text }}</p>
                      </div>

                      <!-- Meta Info -->
                      <div class="d-flex align-items-center gap-3 mt-1">
                        <span class="status-pill text-xs shadow-sm" :class="get_answer_source_badge_class(question.status || 'pending')">
                          <i :class="get_answer_source_icon(question.status || 'pending')"></i>
                          {{ get_answer_source_label(question.status || 'pending') }}
                        </span>
                        <span v-if="question.date_answered" class="text-xs text-muted">
                          <i class="bi bi-clock me-1"></i> {{ format_datetime(question.date_answered) }}
                        </span>
                        <span v-if="question.latest_verification_note" class="text-xs text-orange fst-italic">
                          <i class="bi bi-chat-text me-1"></i> {{ truncate_text(question.latest_verification_note, 40) }}
                        </span>
                      </div>
                    </div>

                    <div class="col-md-5">
                      <!-- Answer Display / Form -->
                      <div v-if="!should_show_inline_input(question)" class="answer-section">
                        <!-- Static Display based on Type -->
                        <div class="display-container bg-light p-2 rounded-small border mb-2 min-height-40 d-flex align-items-center">
                          <!-- File Type -->
                          <div v-if="is_file_type(question.question_type_raw)" class="file-display d-flex align-items-center justify-content-between w-100">
                            <span class="text-xxs text-dark text-truncate me-2" style="max-width: 150px;">
                              {{ question.answer ? get_file_name_from_url(question.answer) : 'No file submitted' }}
                            </span>
                            <button v-if="question.answer" class="btn btn-primary-dark btn-xs py-0 px-2 rounded-small text-xxs" @click.stop="open_file_preview(question.answer, get_file_name_from_url(question.answer))">
                              View
                            </button>
                          </div>

                          <!-- Radio / Single Select -->
                          <div v-else-if="question.question_type_raw === 'single_select'" class="choice-display w-100">
                            <div v-for="choice in question.multiselect_choices" :key="choice" class="d-flex align-items-center gap-2 mb-1">
                              <i class="bi text-xxs" :class="question.answer === choice ? 'bi-record-circle-fill text-primary-dark' : 'bi-circle text-muted opacity-50'"></i>
                              <span class="text-xxs" :class="{ 'fw-bold text-dark': question.answer === choice, 'text-muted opacity-75': question.answer !== choice }">{{ choice }}</span>
                            </div>
                            <div v-if="!question.answer" class="text-xxs text-muted fst-italic">No selection made</div>
                          </div>

                          <!-- Checkbox / Multi Select -->
                          <div v-else-if="is_multiselect_type(question.question_type_raw)" class="choice-display w-100">
                            <div v-for="choice in question.multiselect_choices" :key="choice" class="d-flex align-items-center gap-2 mb-1">
                              <i class="bi text-xxs" :class="is_choice_selected(choice, String(question.answer || '').split(',').map(s=>s.trim())) ? 'bi-check-square-fill text-primary-dark' : 'bi-square text-muted opacity-50'"></i>
                              <span class="text-xxs" :class="{ 'fw-bold text-dark': is_choice_selected(choice, String(question.answer || '').split(',').map(s=>s.trim())), 'text-muted opacity-75': !is_choice_selected(choice, String(question.answer || '').split(',').map(s=>s.trim())) }">{{ choice }}</span>
                            </div>
                            <div v-if="!question.answer" class="text-xxs text-muted fst-italic">No selection made</div>
                          </div>

                          <!-- Textual / Other -->
                          <div v-else class="text-display w-100">
                            <span v-if="question.answer" class="text-xxs text-dark">"{{ question.answer }}"</span>
                            <span v-else class="text-xxs text-muted fst-italic">Not answered</span>
                          </div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="d-flex gap-2">
                          <button class="btn btn-primary-dark btn-xs rounded-small text-xxs shadow-sm" @click="start_editing(question)">
                            <i class="bi bi-pencil-square me-1"></i> {{ question.answer ? 'Update' : 'Submit' }}
                          </button>

                          <button v-if="question.answer" class="btn btn-outline-dark btn-xs rounded-small text-xxs shadow-sm" @click="open_history_modal(question)">
                            <i class="bi bi-clock-history me-1"></i> History
                          </button>

                          <template v-if="question.answer && question.status === 'Submitted'">
                             <button class="btn btn-success btn-xs rounded-small text-xxs shadow-sm" @click="open_verification_modal(question.answer_id, 'Verified')">Approve</button>
                             <button class="btn btn-danger btn-xs rounded-small text-xxs shadow-sm" @click="open_verification_modal(question.answer_id, 'Changes Requested')">Reject</button>
                          </template>
                        </div>
                      </div>

                      <!-- Inline Editing Form -->
                      <div v-else class="edit-section bg-light p-3 rounded-small border shadow-sm">
                        <h6 class="text-xs fw-bold text-uppercase text-muted mb-2 border-bottom pb-1">Edit Answer</h6>

                        <!-- Dynamic Input Types -->
                        <div class="input-container mb-3">
                          <!-- Text / Short Text / Number / Date -->
                          <div v-if="['text', 'short_text', 'short text', 'number', 'date', 'email', 'url'].includes(question.question_type_raw)">
                            <input
                              :type="question.question_type_raw === 'date' ? 'date' : (question.question_type_raw === 'number' ? 'number' : 'text')"
                              class="form-control form-control-sm text-xxs rounded-small p-2 border-secondary"
                              v-model="edit_answer_value"
                              placeholder="Type answer here..."
                            >
                          </div>

                          <!-- Textarea / Long Text -->
                          <div v-else-if="['textarea', 'long_text', 'long text', 'text_area'].includes(question.question_type_raw)">
                            <textarea
                              class="form-control form-control-sm text-xxs rounded-small p-2 border-secondary"
                              rows="3"
                              v-model="edit_answer_value"
                              placeholder="Provide detailed answer..."
                            ></textarea>
                          </div>

                          <!-- Radio (Single Select) -->
                          <div v-else-if="question.question_type_raw === 'single_select'" class="edit-choices">
                            <div v-for="choice in question.multiselect_choices" :key="choice" class="form-check mb-1">
                              <input class="form-check-input" type="radio" :id="'choice-'+choice" :value="choice" v-model="edit_answer_value">
                              <label class="form-check-label text-xxs" :for="'choice-'+choice">{{ choice }}</label>
                            </div>
                          </div>

                          <!-- Checkbox (Multi Select) -->
                          <div v-else-if="is_multiselect_type(question.question_type_raw)" class="edit-choices">
                            <div v-for="choice in question.multiselect_choices" :key="choice" class="form-check mb-1">
                              <input class="form-check-input" type="checkbox" :id="'choice-edit-'+choice" :checked="edit_selected_choices.includes(choice)" @change="toggle_edit_choice(choice)">
                              <label class="form-check-label text-xxs" :for="'choice-edit-'+choice">{{ choice }}</label>
                            </div>
                          </div>

                          <!-- File Upload -->
                          <div v-else-if="is_file_type(question.question_type_raw)">
                            <input type="file" class="form-control form-control-sm text-xxs rounded-small border-secondary" @change="handle_edit_file_change">
                            <div v-if="edit_file" class="mt-1 text-xxs text-success"><i class="bi bi-file-earmark-check me-1"></i>{{ edit_file.name }} selected</div>
                          </div>
                        </div>

                        <!-- Form Actions -->
                        <div class="d-flex gap-2">
                          <button class="btn btn-primary-dark btn-sm rounded-small text-xxs px-3 shadow-sm" @click="submit_answer(question)" :disabled="is_submitting_answer">
                            <span v-if="is_submitting_answer" class="spinner-border spinner-border-sm me-1"></span>
                            Save
                          </button>
                          <button class="btn btn-outline-secondary btn-sm rounded-small text-xxs px-3 border-secondary" @click="cancel_editing">Cancel</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Portals for Modals -->
  <teleport to="body">
    <!-- File Preview -->
    <FilePreviewComponent
      v-if="show_file_preview"
      :presigned_url="preview_file_url"
      :file_name="preview_file_name"
      @close="close_file_preview"
      :key="preview_file_url"
    />

    <!-- Verification Note Modal -->
    <div v-if="show_verification_modal" class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow-lg rounded-lg bg-white">
          <div class="modal-header bg-dark text-white p-2 px-3 cute-header">
            <h6 class="modal-title text-xxs fw-bold tracking-wider">{{ verifying_status === 'Verified' ? 'APPROVE SUBMISSION' : 'REJECT SUBMISSION' }}</h6>
            <button type="button" class="btn-close btn-close-white" @click="close_verification_modal"></button>
          </div>
          <div class="modal-body p-4">
            <label class="text-xxs fw-bold text-muted mb-2 text-uppercase">Internal Note / Reason</label>
            <textarea
              class="form-control form-control-sm text-xs rounded-small p-3 border-2"
              rows="4"
              v-model="verification_note"
              placeholder="Provide context for this decision..."
            ></textarea>
          </div>
          <div class="modal-footer p-3 border-0 gap-2">
            <button class="btn btn-outline-secondary btn-sm rounded-small text-xxs px-4" @click="close_verification_modal">Cancel</button>
            <button
              class="btn btn-sm rounded-small text-xxs shadow-sm px-4"
              :class="verifying_status === 'Verified' ? 'btn-success' : 'btn-danger'"
              @click="submit_verification"
              :disabled="is_verifying"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show"></div>
    </div>

    <!-- History Modal -->
    <ComplianceAnswerHistory
      v-if="show_history_modal"
      :question="history_question"
      :entity_profile_id="entity_profile_id"
      @close="show_history_modal = false"
    />
  </teleport>
</div>